import { MAX_ACCEPTED_PROPOSALS, type ClientProposal } from '@hemmafixa/shared';
import {
  Avatar,
  Badge,
  Button,
  Card,
  DescriptionList,
  DetailSection,
  EmptyState,
  FormField,
  InlineConfirm,
  InlinePanel,
  InlinePanelFooter,
  StatusBadge,
  Textarea,
  cn,
  toast,
} from '@hemmafixa/ui';
import { BadgeCheck, EyeOff, HandCoins } from 'lucide-react';
import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { fill, useI18n } from '@/i18n/context';
import { useCategoryLabel } from '../shared/catalogue';
import { ContactDetailsList } from '../shared/contact-details';
import { QueryView } from '../shared/query-view';
import { ReportButton, ReportPanel } from '../shared/report-button';
import { useStatuses } from '../shared/statuses';
import { useAcceptProposal, useDeclineProposal, useJobProposals } from './api';

/** Proposals still in play (and the chosen one) come first, side by side; declined and closed ones below them. */
const isActive = (proposal: ClientProposal) =>
  proposal.status === 'submitted' || proposal.status === 'accepted' || proposal.status === 'chosen';

/** The proposals on a published job, to compare, accept (P5-02) and decline (P4-07). */
export function JobProposals({ jobId }: { jobId: string }) {
  const { t } = useI18n();
  const c = t.client.job;
  const query = useJobProposals(jobId);
  const [declining, setDeclining] = useState<ClientProposal | null>(null);
  const [accepting, setAccepting] = useState<ClientProposal | null>(null);
  const [reporting, setReporting] = useState<string | null>(null);
  const accept = useAcceptProposal(jobId);
  const max = { max: MAX_ACCEPTED_PROPOSALS };

  /** Accepting, declining and reporting open inside the proposal's card */
  const panelFor = (proposal: ClientProposal) =>
    accepting?.id === proposal.id ? (
      <InlineConfirm
        open
        onOpenChange={(open) => !open && setAccepting(null)}
        title={fill(c.acceptTitle, { company: proposal.provider.companyName })}
        description={fill(c.acceptText, max)}
        confirmLabel={c.accept}
        cancelLabel={t.common.cancel}
        loading={accept.isPending}
        onConfirm={() =>
          accept.mutate(proposal.id, {
            onSuccess: () => {
              setAccepting(null);
              toast.success(c.acceptedToast);
            },
            onError: () => toast.error(fill(c.acceptFailed, max)),
          })
        }
      />
    ) : declining?.id === proposal.id ? (
      <DeclinePanel jobId={jobId} proposal={proposal} onClose={() => setDeclining(null)} />
    ) : reporting === proposal.id ? (
      <ReportPanel target="proposal" targetId={proposal.id} onClose={() => setReporting(null)} />
    ) : null;

  return (
    <DetailSection title={fill(c.proposalsTitle, { count: query.data?.length ?? 0 })}>
      <QueryView query={query}>
        {(proposals) => {
          if (proposals.length === 0) {
            return (
              <EmptyState
                icon={HandCoins}
                title={c.noProposals}
                description={c.noProposalsText}
                className="py-8"
              />
            );
          }
          const active = proposals.filter(isActive);
          const others = proposals.filter((proposal) => !isActive(proposal));
          return (
            <div className="@container grid gap-5">
              {active.length > 0 && <p className="text-sm text-muted-foreground">{c.proposalsText}</p>}
              {active.length > 0 && (
                <ul className="grid gap-4 @xl:grid-cols-2">
                  {active.map((proposal) => (
                    <li key={proposal.id}>
                      <ProposalCard
                        proposal={proposal}
                        onAccept={() => setAccepting(proposal)}
                        onDecline={() => setDeclining(proposal)}
                        onReport={() => setReporting(proposal.id)}
                        panel={panelFor(proposal)}
                      />
                    </li>
                  ))}
                </ul>
              )}
              {others.length > 0 && (
                <div className="grid gap-3">
                  <h3 className="text-sm font-semibold">
                    {fill(c.otherProposals, { count: others.length })}
                  </h3>
                  <ul className="grid gap-4 @xl:grid-cols-2">
                    {others.map((proposal) => (
                      <li key={proposal.id}>
                        <ProposalCard
                          proposal={proposal}
                          onReport={() => setReporting(proposal.id)}
                          panel={panelFor(proposal)}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          );
        }}
      </QueryView>
    </DetailSection>
  );
}

interface ProposalCardProps {
  proposal: ClientProposal;
  onAccept?: () => void;
  onDecline?: () => void;
  onReport: () => void;
  /** Accept or decline form; replaces the card's buttons while open */
  panel?: ReactNode;
}

function ProposalCard({ proposal, onAccept, onDecline, onReport, panel }: ProposalCardProps) {
  const { t, money, date } = useI18n();
  const statuses = useStatuses();
  const categoryLabel = useCategoryLabel();
  const c = t.client.job;
  const final = !isActive(proposal);

  return (
    <Card className={final ? 'grid h-full min-w-0 gap-3 p-4 opacity-75' : 'grid h-full min-w-0 gap-3 p-4'}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <Avatar seed={proposal.provider.id} name={proposal.provider.companyName} kind="company" />
          <div className="grid min-w-0 gap-0.5">
            <p className="font-semibold">{proposal.provider.companyName}</p>
            <p className="inline-flex items-center gap-1 text-xs text-success">
              <BadgeCheck className="size-3.5" aria-hidden />
              {c.verified}
            </p>
          </div>
        </div>
        <StatusBadge status={proposal.status} statuses={statuses.proposal} />
      </div>
      <div className="grid gap-0.5">
        <p className="text-xs text-muted-foreground">{proposal.priceToOre ? c.priceRange : c.fixedPrice}</p>
        <p className="text-xl font-bold tabular-nums">
          {money(proposal.priceFromOre)}
          {proposal.priceToOre ? ` – ${money(proposal.priceToOre)}` : ''}
        </p>
      </div>
      {(proposal.earliestStart || proposal.durationDays || proposal.includes) && (
        <DescriptionList
          items={[
            ...(proposal.earliestStart
              ? [{ label: c.startLabel, value: fill(c.startFrom, { date: date(proposal.earliestStart) }) }]
              : []),
            ...(proposal.durationDays
              ? [{ label: c.durationLabel, value: fill(c.duration, { days: proposal.durationDays }) }]
              : []),
            ...(proposal.includes ? [{ label: c.includes, value: proposal.includes }] : []),
          ]}
        />
      )}
      <p className="whitespace-pre-line text-sm leading-relaxed">{proposal.message}</p>
      {proposal.provider.categorySlugs.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {proposal.provider.categorySlugs.slice(0, 4).map((slug) => (
            <Badge key={slug}>{categoryLabel(slug)}</Badge>
          ))}
        </div>
      )}
      {proposal.status === 'declined' && proposal.declineReason && (
        <p className="text-sm">
          <span className="font-medium">{c.yourReason}:</span> {proposal.declineReason}
        </p>
      )}
      {proposal.contact && <ContactDetailsList contact={proposal.contact} />}
      {proposal.status === 'submitted' && (
        <p className="flex items-start gap-2 text-xs text-muted-foreground">
          <EyeOff className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {c.contactHidden}
        </p>
      )}
      {panel}
      <div
        className={cn(
          'mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4',
          panel && 'hidden',
        )}
      >
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          {fill(c.sentAt, { date: date(proposal.createdAt) })}
          <ReportButton compact onClick={onReport} />
        </span>
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to={`/client/jobs/providers/${proposal.provider.id}`}>{c.viewProfile}</Link>
          </Button>
          {proposal.conversationId && (
            <Button asChild size="sm">
              <Link to={`/client/messages?c=${proposal.conversationId}`}>{t.messages.open}</Link>
            </Button>
          )}
          {proposal.status === 'submitted' && onDecline && (
            <Button variant="outline" size="sm" className="text-danger" onClick={onDecline}>
              {c.decline}
            </Button>
          )}
          {proposal.status === 'submitted' && onAccept && (
            <Button size="sm" onClick={onAccept}>
              {c.accept}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Declining takes an optional reason, which the provider sees. */
function DeclinePanel({
  jobId,
  proposal,
  onClose,
}: {
  jobId: string;
  proposal: ClientProposal;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const c = t.client.job;
  const decline = useDeclineProposal(jobId);
  const [reason, setReason] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    decline.mutate(
      { id: proposal.id, reason: reason.trim() || null },
      {
        onSuccess: () => {
          onClose();
          toast.success(c.declinedToast);
        },
        onError: () => toast.error(c.declineFailed),
      },
    );
  };

  return (
    <InlinePanel
      title={fill(c.declineTitle, { company: proposal.provider.companyName })}
      description={c.declineText}
      onClose={onClose}
      onSubmit={submit}
      tone="danger"
      closeLabel={t.common.cancel}
    >
      <FormField label={c.declineReason} hint={c.declineReasonHint}>
        <Textarea
          rows={3}
          maxLength={500}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </FormField>
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t.common.cancel}
        </Button>
        <Button type="submit" variant="danger" loading={decline.isPending}>
          {c.decline}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}
